import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider, useT } from "@/i18n";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Matters from "@/pages/Matters";
import MatterDetail from "@/pages/MatterDetail";
import Timer from "@/pages/Timer";
import Invoices from "@/pages/Invoices";
import InvoiceForm from "@/pages/InvoiceForm";
import InvoiceDetail from "@/pages/InvoiceDetail";
import Settings from "@/pages/Settings";
import Reports from "@/pages/Reports";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import Receivables from "@/pages/Receivables";
import ImportedInvoiceDetail from "@/pages/ImportedInvoiceDetail";
import Import from "@/pages/Import";
import Receipts from "@/pages/Receipts";
import Calendar from "@/pages/Calendar";

function Protected({ children }) {
  const { user } = useAuth();
  const { t } = useT();
  const location = useLocation();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="auth-loading">
        <div className="text-slate-500 text-sm tracking-wide">{t("auth.loading")}</div>
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
}

function App() {
  useEffect(() => {
    document.title = "RPLAW Case & Deadline Manager";
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <LanguageProvider>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route
                element={
                  <Protected>
                    <Layout />
                  </Protected>
                }
              >
                <Route path="/" element={<Dashboard />} />
                <Route path="/matters" element={<Matters />} />
                <Route path="/matters/:id" element={<MatterDetail />} />
                <Route path="/timer" element={<Timer />} />
                <Route path="/calendar" element={<Calendar />} />
                <Route path="/invoices" element={<Invoices />} />
                <Route path="/invoices/new" element={<InvoiceForm />} />
                <Route path="/invoices/:id" element={<InvoiceDetail />} />
                <Route path="/invoices/:id/edit" element={<InvoiceForm />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/clients" element={<Clients />} />
                <Route path="/clients/:id" element={<ClientDetail />} />
                <Route path="/receivables" element={<Receivables />} />
                <Route path="/receipts" element={<Receipts />} />
                <Route path="/imported-invoices/:id" element={<ImportedInvoiceDetail />} />
                <Route path="/import" element={<Import />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <Toaster position="top-right" richColors />
          </LanguageProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
